import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users, 
  Download,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  UserCheck,
  UserX,
  RefreshCw
} from 'lucide-react';

export default function AdminParticipants() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [participationFilter, setParticipationFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const data = await api.getAdminParticipants(statusFilter, participationFilter);
      setParticipants(data.participants || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar participantes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
  }, [statusFilter, participationFilter]);

  const handleExportCSV = async () => {
    try {
      const data = await api.exportParticipantsCSV();
      
      // Build CSV string
      let csv = data.headers.join(',') + '\n';
      data.rows.forEach(row => {
        csv += row.map(cell => `"${cell}"`).join(',') + '\n';
      });
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = data.filename;
      link.click();
      
      toast.success('CSV exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar CSV');
    }
  };

  const filteredParticipants = participants.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    return (
      p.full_name?.toLowerCase().includes(searchLower) ||
      p.phone?.includes(searchTerm) ||
      p.email?.toLowerCase().includes(searchLower) ||
      p.city?.toLowerCase().includes(searchLower)
    );
  });

  const getClassificationBadge = (classification) => {
    switch (classification) {
      case 'Recurrente':
        return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Recurrente</span>;
      case 'Inactivo':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Inactivo</span>;
      default:
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Nuevo</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    try {
      return new Date(dateStr).toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Participantes</h1>
                <p className="text-sm text-slate-500">{total} registrados</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchParticipants}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button size="sm" onClick={handleExportCSV} className="btn-primary">
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, teléfono, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                >
                  <Users className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant={statusFilter === 'active' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('active')}
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  Activos
                </Button>
                <Button
                  variant={statusFilter === 'inactive' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('inactive')}
                >
                  <UserX className="w-4 h-4 mr-1" />
                  Inactivos
                </Button>
              </div>

              {/* Participation Filter */}
              <div className="flex gap-2">
                <Button
                  variant={participationFilter === 'high' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setParticipationFilter(participationFilter === 'high' ? 'all' : 'high')}
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Alta participación
                </Button>
                <Button
                  variant={participationFilter === 'low' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setParticipationFilter(participationFilter === 'low' ? 'all' : 'low')}
                >
                  <TrendingDown className="w-4 h-4 mr-1" />
                  Baja participación
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{total}</p>
              <p className="text-xs text-slate-500">Total Registrados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {participants.filter(p => p.has_active_tickets).length}
              </p>
              <p className="text-xs text-slate-500">Con Tickets Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {participants.filter(p => p.classification === 'Recurrente').length}
              </p>
              <p className="text-xs text-slate-500">Recurrentes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">
                {participants.reduce((sum, p) => sum + p.total_historical_tickets, 0)}
              </p>
              <p className="text-xs text-slate-500">Tickets Históricos</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                <p className="mt-2 text-slate-500">Cargando participantes...</p>
              </div>
            ) : filteredParticipants.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto text-slate-300" />
                <p className="mt-2 text-slate-500">No se encontraron participantes</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Usuario</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Ciudad</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Contacto</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-600">Activos</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-600">Histórico</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-600">Eventos</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Última Act.</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-600">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredParticipants.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{p.full_name || 'Sin nombre'}</p>
                          <p className="text-xs text-slate-400">Desde {formatDate(p.created_at)}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{p.city || '-'}</td>
                        <td className="px-4 py-3">
                          <p className="text-slate-900">{p.phone || '-'}</p>
                          <p className="text-xs text-slate-400">{p.email || 'Sin email'}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.has_active_tickets ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-4 h-4" />
                              {p.active_tickets_count}
                            </span>
                          ) : (
                            <span className="text-slate-400">
                              <XCircle className="w-4 h-4 inline" />
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-medium">{p.total_historical_tickets}</td>
                        <td className="px-4 py-3 text-center">{p.events_participated}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(p.last_participation)}</td>
                        <td className="px-4 py-3 text-center">
                          {getClassificationBadge(p.classification)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
